/* eslint-disable @next/next/no-img-element */
import React, { useState } from "react";
import { useAppSelector } from "@/lib/redux/hooks";
import Form from "../Contacts/Form";
import SVGCross from "../../lib/svg/SVGCross";
import { sanityClient } from "@/lib/sanity/server";
import { Subtitle, Title } from "../Contacts/Form/styles";
import { useRouter } from "next/router";
import InformationModule from "@/components/InformationModule";
import { useTranslation } from "@/lib/translations";

import {
  WishListContainer,
  WishListWrapper,
  FormWrapper,
  Divider,
  WSCloseButton,
  WSProductsTitle,
  WSToggleLabel,
  WSMobileLayer,
  WSToggleLabelWrapper,
} from "./styles";
import { groq } from "next-sanity";
import useSWR from "swr";
import List from "@/components/Wishlist/List";

type Props = {
  page: any;
  queryParams: {
    locale: string;
    defaultLocale: string;
  };
};

const fetcher = async (
  query: string,
  params: { locale: string; defaultLocale: string; queryIds: string }
) => {
  return sanityClient.fetch(query, params);
};

const query = groq`
*[_type == "product" && _id in $ids]{
  _id,
  title,
  thumbnail {
    asset-> {
      url,
      metadata,
      "alt": coalesce(alt[$locale], alt[$defaultLocale])
    }
  },
  "application": parameters[attribute._ref == "route-of-administration"][0] {
    "name": attribute->.name,
    values,
  },
}
`;

const Wishlist: React.FC<Props> = (props) => {
  const { page, queryParams } = props;
  const [showWishList, setshowWishList] = useState(false);
  const wishlist = useAppSelector((state) => state.wishlist);
  const productIds = wishlist.lines.map((line) => line.productID);

  const router = useRouter();
  const { locale = "it" } = router;

  const t = {
    emptyTitle: useTranslation("wishlist.emptyTitle", locale) || "",
    emptySubTitle: useTranslation("wishlist.emptySubTitle", locale) || "",
    emptyButtonText: useTranslation("wishlist.emptyButtonText", locale) || "",
    successTitle: useTranslation("wishlist.successTitle", locale) || "",
    successSubTitle: useTranslation("wishlist.successSubTitle", locale) || "",
    successButtonText: useTranslation("wishlist.successButtonText", locale) || "",
    selectedProducts: useTranslation("wishlist.selectedProducts", locale) || "",
  };

  const { data: products } = useSWR(
    [query, { ...queryParams, ids: productIds }],
    fetcher,
    {
      onError(err) {
        console.error(err);
      },
    }
  );

  const toggleWishList = () => {
    setshowWishList(!showWishList);
  };

  if (router.query.success) {
    return (
      <InformationModule
        title={t.successTitle}
        subTitle={t.successSubTitle}
        buttonText={t.successButtonText}
        redirectTo={"/products"}
      />
    );
  }

  if (wishlist.lines?.length == 0) {
    return (
      <InformationModule
        title={t.emptyTitle}
        subTitle={t.emptySubTitle}
        buttonText={t.emptyButtonText}
        redirectTo={"/products"}
      />
    );
  }

  const wishlistReadableValue = wishlist.lines.map((line) => {
    return `${line.productName}: ${line.quantity}`
  }).join("; ")

  return (
    <WishListContainer>
      <div>
        {page?.form.heading && <Title as={"h1"}>{page.form.heading}</Title>}
        <WSToggleLabelWrapper>
          {page?.form.shortText && <Subtitle>{page.form.shortText}</Subtitle>}
          <WSToggleLabel onClick={toggleWishList}>
            {t.selectedProducts} ({products?.length})
          </WSToggleLabel>
        </WSToggleLabelWrapper>

        <FormWrapper>
          <Form
            form={page.form}
            wishListJsonStringify={wishlistReadableValue}
            hideTitle={true}
            redirectonSuccess="/wishlist?success=true?"
          />
        </FormWrapper>
      </div>
      {showWishList && <WSMobileLayer onClick={toggleWishList} />}
      <WishListWrapper showWishList={showWishList}>
        <>
          <WSProductsTitle>
            {`${t.selectedProducts} `}
            <WSCloseButton onClick={toggleWishList}>
              <SVGCross />
            </WSCloseButton>
          </WSProductsTitle>
          <Divider />
          <List products={products} />
        </>
      </WishListWrapper>
    </WishListContainer>
  );
};

export default Wishlist;
